import React from 'react';
import { Award, Leaf, CheckCircle2, Download, FileText } from 'lucide-react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';

const HighOleicDetail = ({ product }) => {
  if (!product.organicVariants) return null;

  const pdfDownloads = [
    {
      language: 'Chinese',
      languageNative: '中文',
      flag: '🇨🇳',
      file: 'https://customer-assets.emergentagent.com/job_eyco-refinery/artifacts/gf4x4367_HOSO_Product_Introduction_ZH.pdf',
      code: 'ZH'
    },
    {
      language: 'Thai',
      languageNative: 'ไทย',
      flag: '🇹🇭',
      file: 'https://customer-assets.emergentagent.com/job_eyco-refinery/artifacts/nzku5wrt_HOSO_Product_Introduction_TH.pdf',
      code: 'TH'
    },
    {
      language: 'Japanese',
      languageNative: '日本語',
      flag: '🇯🇵',
      file: 'https://customer-assets.emergentagent.com/job_eyco-refinery/artifacts/xwais0kp_HOSO_Product_Introduction_JA.pdf',
      code: 'JA'
    },
    {
      language: 'Korean',
      languageNative: '한국어',
      flag: '🇰🇷',
      file: 'https://customer-assets.emergentagent.com/job_eyco-refinery/artifacts/mn5edfab_HOSO_Product_Introduction_KO.pdf',
      code: 'KO'
    }
  ];

  return (
    <div className="mt-8 space-y-10">
      {/* PDF Download Section */}
      <div>
        <Card className="bg-gradient-to-br from-indigo-50 to-blue-50 border-2 border-indigo-300 shadow-xl">
          <CardContent className="p-8">
            <div className="text-center mb-6">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-indigo-100 rounded-full mb-4">
                <FileText className="w-8 h-8 text-indigo-600" />
              </div>
              <h3 className="text-3xl font-bold text-gray-900 mb-2">
                Product Information in Your Language
              </h3>
              <p className="text-lg text-gray-700">
                Download detailed High Oleic Sunflower Oil specifications and benefits
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-4 max-w-3xl mx-auto">
              {pdfDownloads.map((pdf) => (
                <a
                  key={pdf.code}
                  href={pdf.file}
                  download
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block"
                >
                  <Card className="hover:shadow-xl transition-all duration-300 hover:-translate-y-1 border-2 hover:border-indigo-400 cursor-pointer">
                    <CardContent className="p-6">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-4">
                          <div className="text-5xl">{pdf.flag}</div>
                          <div>
                            <h4 className="text-lg font-bold text-gray-900">{pdf.language}</h4>
                            <p className="text-sm text-gray-600">{pdf.languageNative}</p>
                          </div>
                        </div>
                        <div className="flex items-center space-x-2 text-indigo-600">
                          <Download className="w-5 h-5" />
                          <span className="text-sm font-medium">PDF</span>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </a>
              ))}
            </div>

            <p className="text-center text-sm text-gray-600 mt-6">
              Click to download product introduction in your preferred language
            </p>
          </CardContent>
        </Card>
      </div>
      {/* Organic Variants Section */}
      <div>
        <h4 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
          <Leaf className="w-6 h-6 mr-2 text-green-600" />
          Organic High Oleic Sunflower Oil Variants
        </h4>
        
        <div className="grid md:grid-cols-2 gap-6">
          {product.organicVariants.map((variant) => (
            <Card key={variant.type} className="overflow-hidden border-2 hover:shadow-xl transition-all group">
              <div className="relative h-56">
                <img 
                  src={variant.image}
                  alt={variant.type}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent"></div>
                <div className="absolute bottom-4 left-4 right-4">
                  <h5 className="text-xl font-bold text-white mb-2">{variant.type}</h5>
                </div>
              </div>
              
              <CardContent className="p-6">
                <p className="text-gray-700 mb-4 leading-relaxed">
                  {variant.description}
                </p>

                {/* Certifications */}
                <div className="mb-4">
                  <div className="flex items-center mb-3">
                    <Award className="w-5 h-5 text-green-600 mr-2" />
                    <h6 className="font-semibold text-gray-900">Organic Certifications</h6>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {variant.certifications.map((cert) => (
                      <span 
                        key={cert}
                        className="px-3 py-1.5 bg-green-100 text-green-700 rounded-full text-sm font-medium border border-green-300"
                      >
                        ✓ {cert}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Features */}
                <div>
                  <h6 className="font-semibold text-gray-900 mb-3">Key Features</h6>
                  <ul className="space-y-2">
                    {variant.features.map((feature) => (
                      <li key={feature} className="flex items-start text-sm">
                        <CheckCircle2 className="w-4 h-4 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                        <span className="text-gray-600">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>

      {/* Organic Standards Info */}
      <Card className="bg-gradient-to-br from-green-50 to-emerald-50 border-2 border-green-300">
        <CardContent className="p-8">
          <div className="flex items-start space-x-4">
            <div className="flex-shrink-0">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
                <Award className="w-8 h-8 text-green-600" />
              </div>
            </div>
            <div>
              <h3 className="text-2xl font-bold text-gray-900 mb-3">Certified Organic Quality</h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                Our organic high oleic sunflower oil products are certified according to both EU Organic Regulations 
                and USDA National Organic Program (NOP) standards, ensuring the highest quality organic products 
                for international markets.
              </p>
              <div className="grid md:grid-cols-2 gap-4 mt-4">
                <div className="bg-white/60 p-4 rounded-lg">
                  <h4 className="font-semibold text-gray-900 mb-2">EU Organic Standards</h4>
                  <p className="text-sm text-gray-600">Compliant with European Union organic certification requirements</p>
                </div>
                <div className="bg-white/60 p-4 rounded-lg">
                  <h4 className="font-semibold text-gray-900 mb-2">NOP (USDA) Standards</h4>
                  <p className="text-sm text-gray-600">Certified under United States Department of Agriculture organic program</p>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default HighOleicDetail;
